import _ from 'lodash';

import stringToPath from 'lodash/_stringToPath';

import { FetchVal, StoreVal } from 'assets/js/api/settings';

const state = {};

const getters = {
  // Return an item from the keystore for settings. Take an argument 'key'
  // which is the backend key e.g. 'siteIdFoo=' and then a path to local data
  // data within that JSON object.
  GetKeyStore: (stateArg) =>
    (key, path) => {
      let val = null;
      if (_.isUndefined(path) || _.isNull(path)) val = _.get(stateArg, key);
      if (!_.isUndefined(path) && !_.isNull(path)) {
        val = _.get(stateArg, `${key}.${path}`);
      }

      // Null values are not allowed.
      if (_.isNull(val) || _.isUndefined(val)) {
        const err = Error('GetKeyStore: key or path ' +
          `'${key}.${path}' not found`);
        err.name = 'KeyError';
        throw err;
      }
      return val;
    },
};

const actions = {
  // ForceStoreKey pushes a backend storage POST of the given key for the
  // current value of the key in the store.
  async ForceStoreKey({ commit }, { key }) {
    const keyValueCopy = _.cloneDeep(state[key]);
    await StoreVal(key, keyValueCopy, true);
  },

  // KeyStoreReset resets the keystore to its starting state.
  KeyStoreReset({ commit }) {
    commit('RESET_KEY_STORE');
  },

  // MutateKeyStore mutates a keystore value for some key both locally and in
  // the backend.
  async MutateKeyStore({ commit }, { key, path, value, storeValue = true }) {
    if (_.isNull(value) || _.isUndefined(value)) {
      throw Error('MutateKeyStore: no value given');
    }

    commit('MUTATE_KEY_STORE', { key, path, value });
    const keyValueCopy = _.cloneDeep(state[key]);
    if (storeValue) await StoreVal(key, keyValueCopy, true);
  },

  // PopulateKeyStore synchronises the local keystore with the remote keystore
  // for some value.
  async PopulateKeyStore({ commit }, key, defaultValueArg) {
    // Don't refetch if it's already populated.
    if (!_.isUndefined(_.get(state, key))) return;

    try {
      const value = await FetchVal(key, true);
      commit('POPULATE_KEY_STORE', { key, value });
    } catch (err) {
      // Invalid JSON or a previously unpopulated key becomes an empty object
      // with a POST, unless the default value is specified.
      let defaultValue = defaultValueArg;
      if (_.isUndefined(defaultValue) || _.isNull(defaultValue)) {
        defaultValue = {};
      }
      commit('MUTATE_KEY_STORE', { key, value: defaultValue });
      const keyValueCopy = _.cloneDeep(state[key]);
      await StoreVal(key, keyValueCopy, true);
    }
  },
};

const mutations = {
  MUTATE_KEY_STORE(stateArg, { key, path, value }) {
    const stateLocal = stateArg;
    if (_.isNull(_.get(stateLocal, key, null))) {
      stateLocal[key] = {};
    }

    let pathAsArray = path;
    if (!_.isArray(pathAsArray) && _.isString(pathAsArray)) {
      pathAsArray = stringToPath(path);
    }

    // Lodash's default set will inject values into arrays instead of creating
    // objects within the path and then injecting them into there. We can only
    // do this when there aren't array dereferences in the path, and the path
    // was originally a string. We will rewrite the data as if it were an
    // object the entire way down.
    if (_.isString(path) && !(_.includes(path, '[') || _.includes(path, ']'))) {
      for (let index = 1; index < pathAsArray.length; index += 1) {
        const subPath = pathAsArray.slice(0, index);
        subPath.unshift(key);
        if (_.isArray(_.get(stateLocal, subPath))) {
          _.set(stateLocal, subPath, {});
        }
      }
    }

    if (_.isUndefined(path) || _.isNull(path)) {
      _.set(stateLocal, key, value);
    } else {
      _.set(stateLocal, `${key}.${path}`, value);
    }
  },

  POPULATE_KEY_STORE(stateArg, { key, value }) {
    const stateLocal = stateArg;
    _.set(stateLocal, key, value);
  },

  RESET_KEY_STORE(stateArg) {
    const stateLocal = stateArg;
    _.forEach(_.keys(stateLocal), (key) => delete stateLocal[key]);
  },
};

export default {
  state,
  getters,
  actions,
  mutations,
};
